import { IframeHTMLAttributes, useCallback, useEffect, useRef } from "react";
import { ExtensionUiComponentWithExtensionInfo } from "../../../graphql/generated";

export type ExtensionUIComponentProps = IframeHTMLAttributes<HTMLIFrameElement> & {
  uiComponent: ExtensionUiComponentWithExtensionInfo;
  componentProps?: Record<string, any>;
};

const ExtensionUIComponent = ({
  uiComponent,
  componentProps,
  ...iframeProps
}: ExtensionUIComponentProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const targetOrigin = uiComponent.rootUrl.replace(/\/$/, "");
  const isReadyRef = useRef(false);

  const postProps = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: "EXTENSION_PROPS", props: componentProps },
      targetOrigin
    );
  }, [componentProps, targetOrigin]);

  // Re-post whenever props change and iframe is already ready
  useEffect(() => {
    if (isReadyRef.current) {
      postProps();
    }
  }, [postProps]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (
        event.origin !== targetOrigin ||
        event.data?.type !== "EXTENSION_READY"
      ) {
        return;
      }
      isReadyRef.current = true;
      postProps();
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [postProps, targetOrigin]);

  return (
    <iframe
      ref={iframeRef}
      title="Extension UI Component"
      name="extension-ui-component-iframe"
      src={`${uiComponent.rootUrl}${uiComponent.endpoint}`}
      style={{
        width: "100%",
        height: "100%",
        border: "none",
      }}
      {...iframeProps}
    />
  );
};

export default ExtensionUIComponent;
